import React from 'react';
import './App.css';
import TenantHeader from './TenantHeader';
import { formatListingPricePerMonth, useTenantPortalProfile } from './TenantPortalContext';
import { supabase } from './supabaseClient';

function TenantFeedback() {
  const { profile, property } = useTenantPortalProfile();
  const [category, setCategory] = React.useState('general');
  const [subject, setSubject] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [allowContact, setAllowContact] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState('');
  const [submitOk, setSubmitOk] = React.useState('');

  const unitLine = React.useMemo(() => {
    const name = (property?.name || '').trim() || 'your home';
    const room = (profile.room_label || '').trim();
    return room ? `${name} · ${room}` : name;
  }, [property?.name, profile.room_label]);

  const listingRent = formatListingPricePerMonth(property?.price_per_month);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setSubmitOk('');
    const msg = message.trim();
    if (!msg) {
      setSubmitError('Please enter your feedback.');
      return;
    }
    if (!supabase) {
      setSubmitError('Supabase is not configured.');
      return;
    }
    setSubmitting(true);
    try {
      const { error } = await supabase.from('tenant_feedback').insert({
        prospective_tenant_id: profile.id,
        property_id: profile.property_id || null,
        tenant_name: (profile.full_name || '').trim() || 'Tenant',
        tenant_email: (profile.email || '').trim(),
        room_label: (profile.room_label || '').trim(),
        category,
        subject: subject.trim(),
        message: msg,
        allow_contact: allowContact,
        status: 'new',
      });
      if (error) throw error;
      setSubject('');
      setMessage('');
      setCategory('general');
      setAllowContact(true);
      setSubmitOk('Thank you — your feedback was saved.');
    } catch (err) {
      setSubmitError(
        err?.message ||
          'Could not save. Run sql/add_tenant_feedback_supabase.sql in the Supabase SQL editor.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="tenant-root">
      <TenantHeader />

      <main className="tenant-maint-shell">
        <header className="tenant-maint-header">
          <div>
            <h1 className="tenant-title">Feedback form</h1>
            <p className="tenant-subtitle">
              Signed in as <strong>{profile.full_name}</strong> ({profile.email}) for{' '}
              <strong>{unitLine}</strong>.
              {listingRent ? (
                <>
                  {' '}
                  Listed rent: <strong>{listingRent}</strong>.
                </>
              ) : null}{' '}
              Your message is tied to this account.
            </p>
          </div>
        </header>

        <section className="tenant-maint-form-card">
          <form className="tenant-maint-form" onSubmit={handleSubmit}>
            {submitError ? (
              <p className="properties-save-error" role="alert">
                {submitError}
              </p>
            ) : null}
            {submitOk ? (
              <p className="tenant-card-sub" role="status" style={{ color: '#15803d' }}>
                {submitOk}
              </p>
            ) : null}

            <div className="field-wrapper">
              <label className="field-label" htmlFor="tf-category">
                Topic
              </label>
              <div className="input-shell">
                <select
                  id="tf-category"
                  className="input-element"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                >
                  <option value="general">General feedback</option>
                  <option value="property">Property & facilities</option>
                  <option value="service">Staff & service</option>
                  <option value="noise">Noise or neighbours</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div className="field-wrapper">
              <label className="field-label" htmlFor="tf-subject">
                Subject (optional)
              </label>
              <div className="input-shell">
                <input
                  id="tf-subject"
                  className="input-element"
                  type="text"
                  placeholder="Short summary"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                />
              </div>
            </div>

            <div className="field-wrapper">
              <label className="field-label" htmlFor="tf-message">
                Your feedback
              </label>
              <div className="input-shell tenant-textarea-shell">
                <textarea
                  id="tf-message"
                  className="tenant-textarea"
                  rows={5}
                  required
                  placeholder="Tell us what went well or what we could do better."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
              </div>
            </div>

            <div className="field-wrapper">
              <label className="remember-me">
                <input
                  type="checkbox"
                  checked={allowContact}
                  onChange={(e) => setAllowContact(e.target.checked)}
                />
                <span>It’s OK to contact me about this feedback</span>
              </label>
            </div>

            <div className="tenant-submit-row">
              <button type="submit" className="btn-primary tenant-submit-btn" disabled={submitting}>
                {submitting ? 'Submitting…' : 'Submit feedback'}
              </button>
            </div>
          </form>
        </section>
      </main>
    </div>
  );
}

export default TenantFeedback;
